import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { useAppContext } from '../../../providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Spinner, Alert } from 'react-bootstrap';
import { useRemainingMaterialSummary } from '../../../features/jobs/hooks/useProductionDashboardStats';

echarts.use([TooltipComponent, PieChart, CanvasRenderer]);

const colorKeys = [
  'info-300',
  'warning-300',
  'danger-300',
  'success-300',
  'primary',
  'secondary',
  'info',
  'warning',
  'success'
];

const prettyWireType = wireType => {
  const t = String(wireType || '')
    .trim()
    .toUpperCase();
  if (t === 'SINGLE-WIRE') return 'Solid Copper';
  if (t === 'STRANDED') return 'Stranded Copper';
  if (t === 'MULTI-WIRE') return 'Multi-Wire Copper';
  return 'Copper';
};

const getDefaultOptions = (getThemeColor, chartData) => ({
  color: colorKeys.map(key => getThemeColor(key)),
  tooltip: {
    trigger: 'item',
    formatter: params => {
      const value = Number(params?.value || 0).toLocaleString('ro-RO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3
      });
      return `${params?.name}<br/>Needed: <strong>${value} kg</strong>`;
    }
  },
  responsive: true,
  maintainAspectRatio: false,
  series: [
    {
      name: 'Copper quantity needed',
      type: 'pie',
      radius: ['48%', '90%'],
      startAngle: 30,
      avoidLabelOverlap: false,
      label: {
        show: false,
        position: 'center',
        formatter: '{x|{d}%} \n {y|{b}}',
        rich: {
          x: {
            fontSize: 31.25,
            fontWeight: 800,
            color: getThemeColor('gray-700'),
            padding: [0, 0, 5, 15]
          },
          y: {
            fontSize: 12.8,
            color: getThemeColor('gray-700'),
            fontWeight: 600
          }
        }
      },
      emphasis: {
        label: {
          show: true
        }
      },
      labelLine: {
        show: false
      },
      data: chartData.map(item => ({
        value: Number(item.planned_kg || 0),
        name: `${prettyWireType(item.wireType)} ${item.wireDiameter}`
      }))
    }
  ],
  grid: {
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    containLabel: false
  }
});

const OrdersCopperReviewChart = () => {
  const { getThemeColor } = useAppContext();
  const { data, isLoading, isError, error } = useRemainingMaterialSummary();

  const chartData = data?.copperByDiameter || [];

  if (isLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '390px', width: '100%' }}
      >
        <Spinner animation="border" size="sm" className="me-2" />
        <span>Loading chart...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="mb-0">
        Failed to load copper chart: {error?.message || 'Unknown error'}
      </Alert>
    );
  }

  if (!chartData.length) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-700"
        style={{ minHeight: '390px', width: '100%' }}
      >
        No copper requirement found
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getDefaultOptions(getThemeColor, chartData)}
      style={{ minHeight: '390px', width: '100%' }}
      notMerge={true}
      lazyUpdate={true}
    />
  );
};

export default OrdersCopperReviewChart;
